import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../Onboarding.css';
import { logo } from '../assets/media';

const API = '/api';

const STEPS = ['Welcome', 'Identity', 'Banking', 'Sign & Submit'];

export default function Onboarding() {
  const { token } = useParams();
  const [record, setRecord]       = useState(null);
  const [notFound, setNotFound]   = useState(false);
  const [step, setStep]           = useState(0);
  const [form, setForm]           = useState({ name: '', mobile: '', address: '', bankName: '', bankBranch: '', accountHolder: '', iban: '', swift: '', upiId: '' });
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  useEffect(() => {
    fetch(`${API}/onboarding/${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) { setNotFound(true); return; }
        setRecord(data);
      })
      .catch(() => setNotFound(true));
  }, [token]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (signedFile) => {
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (signedFile) fd.append('file', signedFile);
      const res = await fetch(`${API}/onboarding/${token}`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Submission failed');
      setRecord(r => ({ ...r, status: data.status, nda_number: data.ndaNumber, name: form.name }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (notFound)  return <OBShell><NotFound /></OBShell>;
  if (!record)   return <OBShell><OBLoading /></OBShell>;

  if (record.status === 'submitted')
    return <OBShell><StatusSubmitted name={record.name} /></OBShell>;
  if (record.status === 'nda_ready')
    return <OBShell><NDAReadyView token={token} record={record} onUploaded={() => setRecord(r => ({ ...r, status: 'consultant_signed' }))} /></OBShell>;
  if (record.status === 'consultant_signed')
    return <OBShell><StatusConsultantSigned name={record.name} /></OBShell>;
  if (record.status === 'counter_signed')
    return <OBShell><StatusComplete name={record.name} /></OBShell>;

  return (
    <OBShell>
      <div className="ob-progress">
        {STEPS.map((label, i) => (
          <div key={i} className={`ob-progress-step${i === step ? ' active' : i < step ? ' done' : ''}`}>
            <div className="ob-progress-dot">{i < step ? '✓' : i + 1}</div>
            <span className="ob-progress-label">{label}</span>
            {i < STEPS.length - 1 && <div className="ob-progress-line" />}
          </div>
        ))}
      </div>

      {step === 0 && <StepWelcome onNext={() => setStep(1)} />}
      {step === 1 && <StepIdentity form={form} set={set} onNext={() => setStep(2)} onBack={() => setStep(0)} />}
      {step === 2 && <StepBanking form={form} set={set} onNext={() => setStep(3)} onBack={() => setStep(1)} />}
      {step === 3 && <StepSignAndSubmit token={token} form={form} onBack={() => setStep(2)} onSubmit={handleSubmit} loading={loading} error={error} />}
    </OBShell>
  );
}

function OBShell({ children }) {
  return (
    <div className="ob-root">
      <div className="ob-bg-overlay" />
      <div className="ob-wrap">
        <div className="ob-header">
          <img src={logo} alt="7 Elemente" className="ob-logo" />
          <span className="ob-header-tag">IT Consultant Onboarding</span>
        </div>
        <div className="ob-card">{children}</div>
        <div className="ob-footer">Secure · GDPR Compliant · 7 Elemente GmbH</div>
      </div>
    </div>
  );
}

function StepWelcome({ onNext }) {
  const instructions = [
    { n: '01', title: 'Verify Your Information', body: 'Fill out the onboarding form accurately. This information will be used for your legal contract and all future bank transfers.' },
    { n: '02', title: 'Review the NDA', body: 'A personalized Non-Disclosure & Framework Agreement will be generated based on your details.' },
    { n: '03', title: 'Download & Sign', body: 'Download the PDF, apply your signature (digital or handwritten), and ensure all pages are legible.' },
    { n: '04', title: 'Upload or Email', body: 'Return the signed document via this portal, or email it to info@7elemente.com with your contract number.' },
    { n: '05', title: 'Confirmation', body: 'Once Elizabeth Kuriakose (Managing Director) counter-signs, you will receive a final copy and your consultant account will be activated.' },
  ];
  return (
    <div className="ob-section">
      <h1 className="ob-title">Welcome to 7EITS</h1>
      <p className="ob-subtitle">Please follow these steps to complete your onboarding and compliance requirements.</p>
      <div className="ob-instructions">
        {instructions.map(item => (
          <div key={item.n} className="ob-instr-item">
            <div className="ob-instr-num">{item.n}</div>
            <div>
              <div className="ob-instr-title">{item.title}</div>
              <div className="ob-instr-body">{item.body}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="ob-actions">
        <button className="ob-btn" onClick={onNext}>Begin Onboarding →</button>
      </div>
    </div>
  );
}

function FieldErr({ msg }) {
  if (!msg) return null;
  return <div className="ob-field-error">{msg}</div>;
}

function StepIdentity({ form, set, onNext, onBack }) {
  const [touched, setTouched] = useState({});
  const touch = (f) => setTouched(t => ({ ...t, [f]: true }));

  const errors = {
    name:    !form.name.trim()    ? 'Full legal name is required'
             : form.name.trim().length < 2 ? 'Name must be at least 2 characters' : '',
    address: !form.address.trim() ? 'Full address is required'
             : form.address.trim().length < 10 ? 'Please enter your complete address (street, city, country)' : '',
  };
  const valid = !errors.name && !errors.address;

  const handleNext = () => { setTouched({ name: true, address: true }); if (valid) onNext(); };

  return (
    <div className="ob-section">
      <h2 className="ob-section-title">Legal Identity</h2>
      <p className="ob-section-sub">This information will appear on your NDA & Framework Agreement exactly as entered.</p>
      <div className="ob-form">
        <div className="ob-field">
          <label className="ob-label">Full Legal Name *</label>
          <input
            className={`ob-input${touched.name && errors.name ? ' ob-input--error' : ''}`}
            value={form.name}
            onChange={e => { set('name', e.target.value); touch('name'); }}
            onBlur={() => touch('name')}
            placeholder="As it appears on your ID / passport"
            autoFocus
          />
          <FieldErr msg={touched.name && errors.name} />
        </div>
        <div className="ob-field">
          <label className="ob-label">Mobile Number</label>
          <input className="ob-input" type="tel" value={form.mobile} onChange={e => set('mobile', e.target.value)} placeholder="+49 123 456 7890" />
        </div>
        <div className="ob-field">
          <label className="ob-label">Full Address *</label>
          <textarea
            className={`ob-input ob-textarea${touched.address && errors.address ? ' ob-input--error' : ''}`}
            value={form.address}
            onChange={e => { set('address', e.target.value); touch('address'); }}
            onBlur={() => touch('address')}
            placeholder={'Street & Number\nCity, Postal Code\nCountry'}
            rows={4}
          />
          <FieldErr msg={touched.address && errors.address} />
        </div>
      </div>
      <div className="ob-actions">
        <button className="ob-btn ob-btn-ghost" onClick={onBack}>← Back</button>
        <button className="ob-btn" onClick={handleNext}>Continue →</button>
      </div>
    </div>
  );
}

function StepBanking({ form, set, onNext, onBack }) {
  const [touched, setTouched] = useState({});
  const touch = (f) => setTouched(t => ({ ...t, [f]: true }));

  const errors = {
    bankName:      !form.bankName.trim()      ? 'Bank name is required' : '',
    accountHolder: !form.accountHolder.trim() ? 'Account holder name is required' : '',
    iban:          !form.iban.trim()          ? 'IBAN / account number is required'
                   : form.iban.trim().length < 8 ? 'IBAN must be at least 8 characters' : '',
    swift:         form.swift.trim() && (form.swift.trim().length < 8 || form.swift.trim().length > 11)
                   ? 'SWIFT / BIC code must be 8–11 characters' : '',
  };
  const valid = !errors.bankName && !errors.accountHolder && !errors.iban && !errors.swift;

  const handleNext = () => {
    setTouched({ bankName: true, accountHolder: true, iban: true, swift: !!form.swift.trim() });
    if (valid) onNext();
  };

  return (
    <div className="ob-section">
      <h2 className="ob-section-title">Financial Details</h2>
      <p className="ob-section-sub">Required for payment processing. These details will be included in your NDA document.</p>
      <div className="ob-security-note">
        🔒 Your bank details are transmitted over an encrypted connection and will be removed from our servers immediately after your NDA document is confirmed as generated — in full compliance with GDPR.
      </div>
      <div className="ob-form">
        <div className="ob-field">
          <label className="ob-label">Bank Name *</label>
          <input
            className={`ob-input${touched.bankName && errors.bankName ? ' ob-input--error' : ''}`}
            value={form.bankName}
            onChange={e => { set('bankName', e.target.value); touch('bankName'); }}
            onBlur={() => touch('bankName')}
            placeholder="e.g. Deutsche Bank, HDFC Bank"
          />
          <FieldErr msg={touched.bankName && errors.bankName} />
        </div>
        <div className="ob-field">
          <label className="ob-label">Bank Branch</label>
          <input className="ob-input" value={form.bankBranch} onChange={e => set('bankBranch', e.target.value)} placeholder="e.g. Main Branch, Sector 18" />
        </div>
        <div className="ob-field">
          <label className="ob-label">Account Holder Name *</label>
          <input
            className={`ob-input${touched.accountHolder && errors.accountHolder ? ' ob-input--error' : ''}`}
            value={form.accountHolder}
            onChange={e => { set('accountHolder', e.target.value); touch('accountHolder'); }}
            onBlur={() => touch('accountHolder')}
            placeholder="Exactly as registered on the bank account"
          />
          <FieldErr msg={touched.accountHolder && errors.accountHolder} />
        </div>
        <div className="ob-field">
          <label className="ob-label">IBAN / Account Number *</label>
          <input
            className={`ob-input ob-mono${touched.iban && errors.iban ? ' ob-input--error' : ''}`}
            value={form.iban}
            onChange={e => { set('iban', e.target.value.toUpperCase().replace(/\s/g, '')); touch('iban'); }}
            onBlur={() => touch('iban')}
            placeholder="DE89370400440532013000"
          />
          <FieldErr msg={touched.iban && errors.iban} />
        </div>
        <div className="ob-field">
          <label className="ob-label">SWIFT / BIC / IFSC Code</label>
          <input
            className={`ob-input ob-mono${touched.swift && errors.swift ? ' ob-input--error' : ''}`}
            value={form.swift}
            onChange={e => { set('swift', e.target.value.toUpperCase()); touch('swift'); }}
            onBlur={() => touch('swift')}
            placeholder="DEUTDEDB"
          />
          <FieldErr msg={touched.swift && errors.swift} />
        </div>
        <div className="ob-field">
          <label className="ob-label">UPI ID</label>
          <input className="ob-input ob-mono" value={form.upiId} onChange={e => set('upiId', e.target.value)} placeholder="name@upi" />
        </div>
      </div>
      <div className="ob-actions">
        <button className="ob-btn ob-btn-ghost" onClick={onBack}>← Back</button>
        <button className="ob-btn" onClick={handleNext}>Review & Submit →</button>
      </div>
    </div>
  );
}

function buildNdaHtmlClient(form) {
  const date = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  const name    = form.name || '—';
  const address = (form.address || '—').replace(/\n/g, '<br/>');
  const bankName      = form.bankName || '—';
  const bankBranch    = form.bankBranch || '—';
  const accountHolder = form.accountHolder || '—';
  const iban   = form.iban || '—';
  const swift  = form.swift || '—';
  const upiId  = form.upiId || '—';
  return `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>NDA Preview</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Arial,Helvetica,sans-serif;font-size:11pt;line-height:1.6;color:#222;max-width:800px;margin:0 auto;padding:20px 40px 40px}
.ph{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:10px;margin-bottom:14px;font-size:8.5pt;color:#555;border-bottom:1px solid #ddd}
.logo-t{font-size:17pt;font-weight:700;color:#7b0ea0;letter-spacing:1px;text-align:right}
.logo-s{font-size:7.5pt;letter-spacing:4px;text-transform:uppercase;color:#7b0ea0;text-align:right}
h1{font-size:20pt;font-weight:700;color:#7b0ea0;margin:16px 0 14px}
.intro{font-size:11pt;margin-bottom:14px}
.party{margin-bottom:10px;font-size:11pt;line-height:1.7}
.sec{font-size:11.5pt;font-weight:700;color:#7b0ea0;margin:18px 0 6px}
.clt{font-size:13pt;font-weight:700;color:#222;margin:22px 0 8px;border-bottom:2px solid #ddd;padding-bottom:4px}
ul.d{list-style:none;padding-left:20px;margin:4px 0 6px}
ul.d li{position:relative;margin-bottom:5px}
ul.d li::before{content:'o';position:absolute;left:-16px;color:#555;font-size:10pt}
.bt{width:100%;border-collapse:collapse;margin:8px 0}
.bt td{border:1.5px solid #333;padding:7px 10px;font-size:11pt}
.bt td:first-child{font-weight:700;width:155px;background:#f9f9f9}
.bnote{font-style:italic;font-size:9.5pt;color:#555;margin-top:6px}
.sigs{margin-top:28px}
.sig-row{display:flex;gap:40px}
.sc{flex:1}.sn{font-weight:700;font-size:11.5pt}.sr{font-size:10pt;color:#444;margin-top:1px}
.sl{border-top:1px solid #888;margin-top:44px;padding-top:6px;font-size:10pt;color:#888;width:200px}
.footer{margin-top:36px;border-top:1px solid #ccc;padding-top:8px;text-align:center;font-size:8.5pt;color:#555}
.pb{page-break-before:always;break-before:page}
.wm{position:fixed;left:-60px;top:50%;transform:rotate(-90deg) translateX(-50%);font-size:13pt;font-weight:700;color:rgba(123,14,160,0.12);letter-spacing:4px;white-space:nowrap;pointer-events:none}
@media print{body{padding:15px 30px}.wm{position:fixed}}
</style></head><body>
<div class="wm">Non-Disclosure Agreement</div>
<div class="ph">
  <div><div class="logo-t">7ELEMENTE</div><div class="logo-s">IT SOLUTIONS</div></div>
</div>
<h1>Non-Disclosure Agreement (NDA)</h1>
<p class="intro">This NDA Agreement # <strong>7EITS-NDA-${new Date().getFullYear()}-PREVIEW</strong> is entered into on <strong>${date}</strong> between:</p>
<div class="party"><strong>1. THE COMPANY: 7Elemente IT Solutions UG</strong>, Bahnhofstraße 11, 38642 Goslar, Germany (hereinafter referred to as the "Company").</div>
<div class="party"><strong>2. THE CONSULTANT:</strong> <strong>${name}</strong>,<br/>residing at ${address} (hereinafter referred to as the "Consultant").</div>
<div class="clt">Agreement Clauses</div>
<div class="sec">§ 1. Confidentiality:</div>
<ul class="d"><li>Treat all source code, designs, and client data as strictly confidential.</li><li>Comply with <strong>EU GDPR (DSGVO)</strong> data protection standards.</li><li>Use information only for the assigned project; no third-party disclosure.</li></ul>
<div class="sec">§ 2. Intellectual Property (IP) &amp; Ownership:</div>
<ul class="d"><li><strong>Irrevocable Transfer:</strong> All rights, titles, and interests in work products (code, designs, docs) are transferred to <strong>7Elemente IT Solutions UG</strong>.</li><li><strong>Work for Hire:</strong> The Company holds exclusive, perpetual, worldwide rights to modify, sell, or sublicense the work.</li><li><strong>No Reuse:</strong> Consultant shall not reuse, claim authorship, or sell project materials to third parties.</li></ul>
<div class="sec">§ 3. Non-Circumvention (No Trespassing):</div>
<ul class="d"><li>Consultant shall not directly or indirectly solicit or engage with clients introduced by the Company.</li><li>This restriction is valid during the term and for <strong>24 months</strong> following the final project completion.</li></ul>
<div class="sec">§ 4. Security &amp; Law:</div>
<ul class="d"><li>Maintain professional device security (encryption/passwords) and disclose any Open-Source libraries used.</li><li>Governed by the laws of the <strong>Federal Republic of Germany</strong>; jurisdiction is <strong>Goslar</strong>.</li></ul>
<div class="sec pb">Consultant Onboarding &amp; Bank Data*</div>
<p style="font-style:italic;font-size:10.5pt;color:#444;margin:4px 0 8px">Please provide accurate details for project payments:</p>
<table class="bt">
  <tr><td>Bank Name:</td><td>${bankName}</td></tr>
  <tr><td>Bank Branch:</td><td>${bankBranch}</td></tr>
  <tr><td>Account Holder:</td><td>${accountHolder}</td></tr>
  <tr><td>IBAN / Account #:</td><td>${iban}</td></tr>
  <tr><td>SWIFT / IFSC Code:</td><td>${swift}</td></tr>
  <tr><td>UPI ID:</td><td>${upiId}</td></tr>
</table>
<p class="bnote">Note: *This bank information will be deleted from our server after the Contract is generated.</p>
<div class="sigs">
  <div class="sec">§ 7. Signatures</div>
  <p style="font-size:11pt;margin:10px 0 16px">This agreement is signed and accepted by the undersigned representatives of both parties:</p>
  <div class="sig-row">
    <div class="sc"><div class="sn">Name</div><div class="sr">Consultant</div><br/><div class="sn">${name}</div><div class="sl">Signature</div><div class="sl">Date</div></div>
    <div class="sc"><div class="sn">Elizabeth Kuriakose,</div><div class="sr">IT Director</div><br/><div style="height:22px"></div><div class="sl">Signature</div><div class="sl">Date</div></div>
  </div>
</div>
<div class="footer"><strong>7Elemente IT Solutions UG (haftungsbeschränkt)</strong><br/>Lampestr.12 | Goslar 38640 | Germany | Phone: +49 5321 39 49 49 6 | info@7Elemente.com | www.7elemente.com</div>
</body></html>`;
}

function StepSignAndSubmit({ token, form, onBack, onSubmit, loading, error }) {
  const [file, setFile]       = useState(null);
  const [ndaUrl, setNdaUrl]   = useState('');
  const [ndaHtml, setNdaHtml] = useState('');
  const [fetching, setFetching] = useState(true);
  const [fetchErr, setFetchErr] = useState('');

  useEffect(() => {
    setFetching(true);
    fetch(`${API}/onboarding/${token}/nda-preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: form.name, address: form.address,
        bankName: form.bankName, bankBranch: form.bankBranch,
        accountHolder: form.accountHolder, iban: form.iban,
        swift: form.swift, upiId: form.upiId,
      }),
    })
      .then(async r => {
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          throw new Error(e.error || 'Could not load NDA');
        }
        return r.text();
      })
      .then(html => {
        setNdaHtml(html);
        const blob = new Blob([html], { type: 'text/html' });
        setNdaUrl(URL.createObjectURL(blob));
        setFetching(false);
      })
      .catch(e => { setFetchErr(e.message || 'Could not load NDA preview.'); setFetching(false); });
    return () => { if (ndaUrl) URL.revokeObjectURL(ndaUrl); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePrint = () => {
    const w = window.open('', '_blank');
    if (!w || !ndaHtml) return;
    w.document.write(ndaHtml);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  return (
    <div className="ob-section">
      <h2 className="ob-section-title">Review &amp; Sign Your NDA</h2>
      <p className="ob-section-sub">Your contract is ready with your assigned NDA number. Print it, sign it, then upload and submit below.</p>

      {fetching && <div className="ob-loading">Loading your NDA…</div>}
      {fetchErr && <div className="ob-error">{fetchErr}</div>}
      {!fetching && !fetchErr && ndaUrl && (
        <div className="ob-nda-frame-wrap">
          <iframe src={ndaUrl} className="ob-nda-frame" title="NDA" />
        </div>
      )}

      <div className="ob-nda-action-row" style={{ gap: 10 }}>
        <button className="ob-btn" onClick={handlePrint} disabled={!ndaHtml}>🖨️ Print / Save as PDF</button>
        {ndaUrl && (
          <a className="ob-btn ob-btn-ghost" href={ndaUrl} target="_blank" rel="noopener noreferrer">↗ Open in new tab</a>
        )}
      </div>

      <div className="ob-upload-box">
        <div className="ob-upload-label">Upload Signed Document</div>
        <div className="ob-upload-hint">Print → sign → scan/photo → upload. Or email to info@7Elemente.com after submitting.</div>
        {file && <div style={{ fontSize: 12, color: '#6efcf5', marginTop: 8 }}>📎 {file.name}</div>}
        <label className="ob-btn ob-btn-ghost ob-upload-btn">
          {file ? '📎 Change File' : '⬆ Select Signed PDF'}
          <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" style={{ display: 'none' }}
            onChange={e => setFile(e.target.files[0] || null)} />
        </label>
      </div>

      {error && <div className="ob-error">{error}</div>}
      <p className="ob-disclaimer">By submitting, you confirm all information is accurate and agree to the NDA terms above. You can still upload the signed copy later via this portal link.</p>
      <div className="ob-actions">
        <button className="ob-btn ob-btn-ghost" onClick={onBack} disabled={loading}>← Back</button>
        <button className="ob-btn" onClick={() => onSubmit(file)} disabled={loading || fetching}>
          {loading ? <span className="ob-spinner" /> : 'Submit Onboarding'}
        </button>
      </div>
    </div>
  );
}

function NDAReadyView({ token, record, onUploaded }) {
  const [uploading, setUploading]   = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/onboarding/${token}/upload-signed`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      onUploaded();
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="ob-section">
      <h2 className="ob-section-title">Your NDA is Ready to Sign</h2>
      <p className="ob-section-sub">Contract: <strong style={{ color: '#6efcf5' }}>{record.nda_number}</strong></p>
      <div className="ob-instructions">
        <div className="ob-instr-item">
          <div className="ob-instr-num">01</div>
          <div>
            <div className="ob-instr-title">Open &amp; Print Your NDA</div>
            <div className="ob-instr-body">Click the button below to open your personalized NDA. Use your browser's print function to get a PDF or hard copy.</div>
          </div>
        </div>
        <div className="ob-instr-item">
          <div className="ob-instr-num">02</div>
          <div>
            <div className="ob-instr-title">Sign the Document</div>
            <div className="ob-instr-body">Sign on the signature line. Digital signatures and handwritten signatures on scanned copies are both accepted.</div>
          </div>
        </div>
        <div className="ob-instr-item">
          <div className="ob-instr-num">03</div>
          <div>
            <div className="ob-instr-title">Return the Signed Copy</div>
            <div className="ob-instr-body">Upload below, or email it to <strong>info@7elemente.com</strong> with subject line: <em>{record.nda_number} – Signed</em></div>
          </div>
        </div>
      </div>

      <div className="ob-nda-action-row">
        <a className="ob-btn" href={`${API}/onboarding/${token}/nda`} target="_blank" rel="noopener noreferrer">
          📄 Open &amp; Print NDA
        </a>
      </div>

      <div className="ob-upload-box">
        <div className="ob-upload-label">Upload Signed Document</div>
        <div className="ob-upload-hint">PDF, DOC or DOCX · max 20 MB</div>
        {uploadError && <div className="ob-error" style={{ margin: '8px 0 0' }}>{uploadError}</div>}
        <label className="ob-btn ob-btn-ghost ob-upload-btn" style={{ cursor: uploading ? 'wait' : 'pointer' }}>
          {uploading ? <><span className="ob-spinner" /> Uploading…</> : '⬆ Upload Signed NDA'}
          <input type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
        </label>
      </div>
    </div>
  );
}

function StatusSubmitted({ name }) {
  return (
    <div className="ob-result">
      <div className="ob-result-icon ob-result-icon--success">✓</div>
      <h2 className="ob-result-title">Details Received</h2>
      <p className="ob-result-body">Thank you{name ? `, ${name}` : ''}. We are preparing your personalized NDA & Framework Agreement.</p>
      <p className="ob-result-hint">You will receive an email when your document is ready to sign. Please keep this link — you'll return here to upload your signed copy.</p>
    </div>
  );
}

function StatusConsultantSigned({ name }) {
  return (
    <div className="ob-result">
      <div className="ob-result-icon" style={{ background: 'rgba(59,223,181,0.1)', border: '2px solid rgba(59,223,181,0.4)', color: '#3bdfb5', width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>⏳</div>
      <h2 className="ob-result-title">Awaiting Counter-Signature</h2>
      <p className="ob-result-body">Your signed document has been received{name ? `, ${name}` : ''}. Elizabeth Kuriakose will counter-sign shortly.</p>
      <p className="ob-result-hint">You will receive an email with the fully executed agreement once complete.</p>
    </div>
  );
}

function StatusComplete({ name }) {
  return (
    <div className="ob-result">
      <div className="ob-result-icon ob-result-icon--success">★</div>
      <h2 className="ob-result-title">Agreement Fully Executed</h2>
      <p className="ob-result-body">Welcome{name ? `, ${name}` : ''}! Your consultant account is now active.</p>
      <p className="ob-result-hint">A copy of the executed agreement has been sent to your email. We look forward to working with you.</p>
    </div>
  );
}

function NotFound() {
  return (
    <div className="ob-result">
      <div className="ob-result-icon ob-result-icon--error">✗</div>
      <h2 className="ob-result-title">Link Not Found</h2>
      <p className="ob-result-body">This onboarding link is invalid or has expired.</p>
      <p className="ob-result-hint">Please contact 7 Elemente GmbH at <strong>info@7elemente.com</strong> to request a new invitation.</p>
    </div>
  );
}

function OBLoading() {
  return (
    <div className="ob-result">
      <span className="ob-spinner ob-spinner-lg" />
    </div>
  );
}
